/**
 * EntityRow — borrower Entities list row.
 *
 * 7-column grid: Legal name (mint + formation sub) | Type chip (id/orange) |
 * EIN · State (mono) | Owners (dim) | Active loans chip (mint dot) |
 * Status chip (mint dot) | chevron.
 *
 * Width template: `1.8fr 1fr 1fr 1.8fr 100px 90px 36px`.
 * 44px min-height row to clear the touch-target floor; the whole row is the
 * click affordance (navigates to the entity hub).
 */

'use client';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, baseColors, GlassChip, iconSizes, Typography, useI18n } from '@ops-matrix/design-system-ui';
import type { ReactElement } from 'react';

export interface EntityRowData {
  id: string;
  legalName: string;
  entityType: string;
  ein?: string | null;
  stateOfFormation?: string | null;
  formationDate?: string | null;
  ownerNames: string[];
  activeLoanCount: number;
  status: string;
}

export interface EntityRowProps {
  entity: EntityRowData;
  onClick: (entityId: string) => void;
}

// Spec: LLC → "id" tone (purple/identity, mapped to MUI primary which is
// the Stripe-derived purple-blue), Individual → orange (mapped to MUI warning
// — closest analogue; GlassChip color is constrained to MUI palette keys).
const TYPE_TONE: Record<string, 'primary' | 'warning' | 'default'> = {
  llc: 'primary',
  individual: 'warning',
};

function formatFormationSub(
  entity: EntityRowData,
  t: (key: string, defaultValueOrParams?: string | Record<string, string | number | undefined>) => string,
): string {
  const parts: string[] = [];
  if (entity.stateOfFormation) {
    parts.push(t('consumer.entities.rowFormedSub', { state: entity.stateOfFormation }));
  }
  if (entity.formationDate) {
    const year = new Date(entity.formationDate).getFullYear();
    if (!Number.isNaN(year)) {
      parts.push(String(year));
    }
  }
  return parts.join(' · ');
}

export function EntityRow({ entity, onClick }: EntityRowProps): ReactElement {
  const { t } = useI18n();
  const formationSub = formatFormationSub(entity, t);
  const typeKey = entity.entityType.toLowerCase();
  const typeTone: 'primary' | 'warning' | 'default' = TYPE_TONE[typeKey] ?? 'default';
  const ownersDisplay = entity.ownerNames.length > 0 ? entity.ownerNames.join(', ') : '—';
  const loanCountLabel =
    entity.activeLoanCount > 0
      ? t('consumer.entities.loanCount', { count: entity.activeLoanCount })
      : t('consumer.entities.loanCount', { count: 0 });

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(entity.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick(entity.id);
        }
      }}
      sx={{
        display: 'grid',
        gridTemplateColumns: '1.8fr 1fr 1fr 1.8fr 100px 90px 36px',
        gap: 1.5,
        alignItems: 'center',
        minHeight: 56,
        px: 1.5,
        py: 1.25,
        borderRadius: 1.5,
        cursor: 'pointer',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
        '&:focus-visible': {
          outline: `2px solid ${baseColors.teal[400]}`,
          outlineOffset: -2,
        },
      }}
    >
      {/* Legal name + formation sub */}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: baseColors.teal[400], lineHeight: 1.3 }}>
          {entity.legalName}
        </Typography>
        {formationSub && (
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
            {formationSub}
          </Typography>
        )}
      </Box>

      {/* Type chip */}
      <Box sx={{ minWidth: 0 }}>
        <GlassChip
          label={entity.entityType.toUpperCase()}
          size="small"
          color={typeTone === 'default' ? 'default' : typeTone}
          variant="outlined"
        />
      </Box>

      {/* EIN · State (mono) */}
      <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
        {[entity.ein || '—', entity.stateOfFormation || '—'].join(' · ')}
      </Typography>

      {/* Owners */}
      <Typography variant="caption" noWrap sx={{ color: 'text.secondary' }}>
        {ownersDisplay}
      </Typography>

      {/* Active loans chip */}
      <Box>
        <GlassChip
          label={loanCountLabel}
          size="small"
          color={entity.activeLoanCount > 0 ? 'success' : 'default'}
          sx={{
            '& .MuiChip-label': { fontSize: '0.75rem', fontWeight: 500 },
          }}
        />
      </Box>

      {/* Status chip */}
      <Box>
        <GlassChip
          label={entity.status}
          size="small"
          color={entity.status?.toLowerCase() === 'active' ? 'success' : 'default'}
        />
      </Box>

      {/* Chevron */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ChevronRightIcon sx={{ fontSize: iconSizes.base, color: 'text.disabled' }} />
      </Box>
    </Box>
  );
}
